// frontend/src/components/daytrader/RiskGuard.tsx
//
// The day trader's brakes. Shows today's loss budget (R used / remaining,
// trades today / max) from /api/day-risk/status, a LOCKED banner once a
// limit trips, an editable daily-risk config, and a position-size calculator
// (entry + stop → qty, notional, $ risk, round-trip cost in R).
//
// "Trades today" counts rows in tracked_trades (the "I took this trade" log),
// so the budget only knows about trades you actually logged.

import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import { ShieldAlert, ShieldCheck, ChevronDown, ChevronUp, Calculator, Settings2, Lock } from "lucide-react";
import { Alert, Button, Chip, Label, Meter, NumberField, Separator, Skeleton } from "@heroui/react";
import { DirectionChip, Panel, Pnl } from "@/components/kit";
import { cn } from "@/lib/utils";
import {
  MAX_COST_R, budgetUsedFraction, costTooHigh, formatCountdown, parseNum,
  type DailyRiskConfig, type DailyRiskStatus, type PositionSize,
} from "./dayTrader";
import { DAY_RISK_CHANGED } from "../../hooks/useDayRisk";

const POLL_MS = 30_000;

function useDayRiskStatus() {
  const [status, setStatus] = useState<DailyRiskStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/day-risk/status");
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "status unavailable");
      setStatus(json.status as DailyRiskStatus);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load risk status");
    }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);
  return { status, error, reload: load };
}

function usd(n: number): string {
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: n < 100 ? 2 : 0 })}`;
}
function fmtQty(n: number): string {
  if (n >= 1000) return n.toLocaleString("en-US", { maximumFractionDigits: 1 });
  if (n >= 1) return n.toFixed(3);
  return n.toPrecision(4);
}

export function RiskGuard({ lastPrice, symbol, className = "" }: { lastPrice?: number | null; symbol?: string; className?: string }) {
  const { status, error, reload } = useDayRiskStatus();
  // Collapsible "daily limits" editor (the calculator is always visible).
  const [open, setOpen] = useState(false);

  if (!status) {
    return (
      <Panel title="Daily risk" className={className}>
        {error ? (
          <Alert status="danger">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Risk budget unavailable</Alert.Title>
              <Alert.Description>{error}</Alert.Description>
              <Button size="sm" variant="secondary" className="mt-2" onPress={reload}>Retry</Button>
            </Alert.Content>
          </Alert>
        ) : (
          <div className="space-y-3" aria-busy="true" aria-label="Loading daily risk budget">
            <Skeleton className="h-2 w-full rounded-full" />
            <div className="grid grid-cols-2 gap-2">
              {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
            </div>
            <Skeleton className="h-8 w-full rounded-lg" />
            <Skeleton className="h-8 w-full rounded-lg" />
          </div>
        )}
      </Panel>
    );
  }

  const cfg = status.config;
  const usedR = Math.max(0, cfg.maxDailyLossR - status.remainingR);
  const used = budgetUsedFraction(status);
  const meterColor = status.locked ? "danger" : used >= 0.66 ? "warning" : "success";
  const resetIn = Math.max(0, (status.nextResetMs - Date.now()) / 1000);

  return (
    <Panel
      className={cn(status.locked && "border-danger/60", className)}
      title={
        <span className="flex items-center gap-2">
          {status.locked ? <ShieldAlert className="size-4 text-danger" aria-hidden /> : <ShieldCheck className="size-4 text-muted" aria-hidden />}
          Daily risk
        </span>
      }
      actions={
        <Chip size="sm" variant="soft" color={status.locked ? "danger" : "default"} className="num">
          1R = {usd(status.riskPerTradeUsd)}
        </Chip>
      }
      bodyClassName="space-y-4"
    >
      {status.locked && (
        <Alert status="danger">
          <Alert.Indicator>
            <Lock className="size-4" />
          </Alert.Indicator>
          <Alert.Content>
            <Alert.Title>Locked for today</Alert.Title>
            <Alert.Description>
              {status.reason ?? "daily limit reached"}. Resets in <span className="num">{formatCountdown(resetIn)}</span>.
            </Alert.Description>
          </Alert.Content>
        </Alert>
      )}

      <Meter
        aria-label="Daily loss budget used"
        value={used * 100}
        color={meterColor}
        size="sm"
        valueLabel={<span className="num">{usedR.toFixed(1)} / {cfg.maxDailyLossR}R</span>}
        className="w-full"
      >
        <Label className="text-2xs font-medium uppercase tracking-wide text-muted">Loss budget used</Label>
        <Meter.Output className="text-xs" />
        <Meter.Track>
          <Meter.Fill />
        </Meter.Track>
      </Meter>

      <div className="grid grid-cols-2 gap-2">
        <Mini label="Left today" value={`${status.remainingR.toFixed(1)}R`} tone={status.remainingR < 1 ? "loss" : "fg"} />
        <Mini label="Trades" value={`${status.tradesToday} / ${cfg.maxTradesPerDay}`} tone={status.remainingTrades === 0 ? "loss" : "fg"} />
        <Mini
          label="Realized"
          value={<Pnl value={status.realizedR} suffix="R" digits={2} />}
        />
        <Mini label="Open risk" value={`${status.openRiskR.toFixed(1)}R`} sub={`${status.openTrades} open`} />
      </div>
      <div className="flex items-center justify-between text-2xs text-muted">
        <span>Day resets <span className="num">{String(cfg.resetHourUtc).padStart(2, "0")}:00 UTC</span></span>
        <span className="num text-foreground">{formatCountdown(resetIn)}</span>
      </div>

      <Separator />
      <SizeCalculator lastPrice={lastPrice ?? null} symbol={symbol} riskUsd={status.riskPerTradeUsd} />

      <Separator />
      <div>
        <Button
          size="sm"
          variant="ghost"
          fullWidth
          className="justify-between px-1"
          aria-expanded={open}
          onPress={() => setOpen(v => !v)}
        >
          <span className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-muted">
            <Settings2 className="size-3.5" aria-hidden /> Daily limits
          </span>
          {open ? <ChevronUp className="size-4 text-muted" /> : <ChevronDown className="size-4 text-muted" />}
        </Button>
        {open && (
          <div className="pt-3">
            <ConfigEditor config={cfg} onSaved={reload} />
          </div>
        )}
      </div>
    </Panel>
  );
}

function Mini({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: string; tone?: "fg" | "loss" | "profit" | "warning" }) {
  const c = tone === "loss" ? "text-loss" : tone === "profit" ? "text-profit" : tone === "warning" ? "text-warning" : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-surface-secondary px-2.5 py-2">
      <div className="text-2xs text-muted">{label}</div>
      <div className={cn("num text-sm font-semibold", c)}>{value}</div>
      {sub && <div className="truncate text-2xs text-muted" title={sub}>{sub}</div>}
    </div>
  );
}

/** HeroUI NumberField bound to the string state the sizing/config logic uses (parseNum). */
export function PriceField({
  label, value, onChange, hint, digits = 8, extra,
}: { label: string; value: string; onChange: (v: string) => void; hint?: string; digits?: number; extra?: ReactNode }) {
  const n = parseNum(value);
  const labelId = useId();
  // `extra` (e.g. a Button) must live OUTSIDE NumberField: React Aria's
  // NumberField gives every nested Button its increment/decrement slot
  // context, and a slot-less Button inside it throws at render.
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-2">
        <Label id={labelId} className="text-2xs text-muted" title={hint}>{label}</Label>
        {extra}
      </div>
      <NumberField
        fullWidth
        variant="secondary"
        aria-labelledby={labelId}
        value={n ?? NaN}
        onChange={v => onChange(v == null || Number.isNaN(v) ? "" : String(v))}
        formatOptions={{ useGrouping: false, maximumFractionDigits: digits }}
        minValue={0}
        className="min-w-0"
      >
        <NumberField.Group>
          <NumberField.Input className="num text-[13px]" placeholder="0.00" />
        </NumberField.Group>
      </NumberField>
    </div>
  );
}

// ─── position-size calculator ────────────────────────────────────────────────

function SizeCalculator({ lastPrice, symbol, riskUsd }: { lastPrice: number | null; symbol?: string; riskUsd: number }) {
  const [entry, setEntry] = useState("");
  const [stop, setStop] = useState("");
  const [size, setSize] = useState<PositionSize | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const e = parseNum(entry);
  const s = parseNum(stop);

  useEffect(() => {
    if (e == null || s == null || e <= 0 || s <= 0 || e === s) { setSize(null); setErr(null); return; }
    const ctrl = new AbortController();
    const id = setTimeout(async () => {
      try {
        const res = await fetch("/api/day-risk/size", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entry: e, stop: s }),
          signal: ctrl.signal,
        });
        const json = await res.json();
        if (!json.success) { setSize(null); setErr(json.error || "Invalid input"); return; }
        setSize(json.size as PositionSize);
        setErr(null);
      } catch (x) {
        if (x instanceof DOMException && x.name === "AbortError") return;
        setErr("Sizing failed — check backend connection");
      }
    }, 250);
    return () => { clearTimeout(id); ctrl.abort(); };
  }, [e, s, riskUsd]);

  const side = e != null && s != null && e !== s ? (e > s ? "LONG" : "SHORT") : null;
  const base = symbol?.replace("USDT", "") ?? "units";

  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5">
        <Calculator className="size-3.5 text-muted" aria-hidden />
        <span className="text-2xs font-medium uppercase tracking-wide text-muted">Position size</span>
        {side && <span className="ml-auto"><DirectionChip direction={side} /></span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <PriceField
          label="Entry"
          value={entry}
          onChange={setEntry}
          extra={lastPrice != null && (
            <Button size="sm" variant="ghost" className="h-5 min-w-0 px-1.5 text-3xs" aria-label={`Use last ${base} price as entry`} onPress={() => setEntry(String(lastPrice))}>
              last
            </Button>
          )}
        />
        <PriceField label="Stop" value={stop} onChange={setStop} />
      </div>

      {err && <p className="mt-2 text-2xs text-danger">{err}</p>}
      {size && (
        <>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Mini label="Quantity" value={`${fmtQty(size.qty)} ${base}`} sub={`stop ${size.stopPct.toFixed(2)}% away`} />
            <Mini label="Notional" value={usd(size.notional)} />
            <Mini label="Risk if stopped" value={usd(size.riskUsd)} sub="price risk, before costs" />
            <Mini label="Fees + slippage" value={`${size.costR.toFixed(2)}R`} sub={usd(size.costUsd)} tone={costTooHigh(size.costR) ? "loss" : "fg"} />
          </div>
          {costTooHigh(size.costR) && (
            <Alert status="danger" className="mt-2">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Stop too tight for its costs</Alert.Title>
                <Alert.Description>
                  Round-trip costs are {size.costR.toFixed(2)}R — above the {MAX_COST_R}R limit. Widen the stop (smaller size) or skip.
                </Alert.Description>
              </Alert.Content>
            </Alert>
          )}
        </>
      )}
      {!size && !err && <p className="mt-2 text-2xs text-muted">Type an entry and a stop — size is derived so a stop-out costs 1R.</p>}
    </div>
  );
}

// ─── config editor ───────────────────────────────────────────────────────────

const CONFIG_FIELDS: Array<{ key: keyof DailyRiskConfig; label: string; hint: string }> = [
  { key: "accountSize",     label: "Account ($)",       hint: "Account size used for day-trade sizing (> 0)" },
  { key: "riskPerTradePct", label: "Risk / trade (%)",  hint: "Percent of the account risked per trade, (0, 10]" },
  { key: "maxDailyLossR",   label: "Max daily loss (R)", hint: "Stop trading for the day after this many R lost, (0, 50]" },
  { key: "maxTradesPerDay", label: "Max trades / day",  hint: "Whole number 1–100 (Playbook: 3)" },
  { key: "resetHourUtc",    label: "Day resets (UTC h)", hint: "Integer 0–23" },
];

function ConfigEditor({ config, onSaved }: { config: DailyRiskConfig; onSaved: () => void }) {
  const toDraft = (c: DailyRiskConfig) =>
    Object.fromEntries(CONFIG_FIELDS.map(f => [f.key, String(c[f.key])])) as Record<keyof DailyRiskConfig, string>;
  const [draft, setDraft] = useState(() => toDraft(config));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const dirty = CONFIG_FIELDS.some(f => draft[f.key] !== String(config[f.key]));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch("/api/day-risk/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const json = await res.json();
      if (!json.success) { setMsg({ text: json.error || "Save failed", ok: false }); return; }
      setDraft(toDraft(json.config as DailyRiskConfig));
      setMsg({ text: "Saved", ok: true });
      onSaved();
      window.dispatchEvent(new Event(DAY_RISK_CHANGED)); // the ticket, header and safety limit re-read
    } catch {
      setMsg({ text: "Save failed — check backend connection", ok: false });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        {CONFIG_FIELDS.map(f => (
          <PriceField
            key={f.key}
            label={f.label}
            hint={f.hint}
            digits={4}
            value={draft[f.key]}
            onChange={v => setDraft(d => ({ ...d, [f.key]: v }))}
          />
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" variant="secondary" isDisabled={!dirty || saving} isPending={saving} onPress={save}>
          {saving ? "Saving…" : "Save limits"}
        </Button>
        {dirty && !saving && (
          <Button size="sm" variant="ghost" onPress={() => { setDraft(toDraft(config)); setMsg(null); }}>Reset</Button>
        )}
        {msg && <span className={cn("text-2xs", msg.ok ? "text-profit" : "text-danger")}>{msg.text}</span>}
      </div>
      <p className="mt-2 text-2xs leading-snug text-muted">
        Separate from the desk's Settings. Losses count once a logged trade closes; each open trade reserves 1R. Profits don't raise the loss limit.
      </p>
    </div>
  );
}
